import {Injectable} from '@angular/core';
import {DomSanitizer, SafeUrl} from '@angular/platform-browser';
import {TranslateService} from '@ngx-translate/core';
import moment from 'moment';
import {intersection, some} from 'lodash';
import { Observable } from 'rxjs';

import {environment} from '@sharedenvironment';
import {EntityDto, PatchDto, UserMetaDto, UserServiceClient} from '@sharedrest/identity.restservice';
import {MatrixDto, MatrixServiceClient} from '@sharedcommon/rest/equation.restservice';
import {FunctionUtils} from '@sharedcommon/util/functions.util';
import {UserStore} from '@sharedcommon/store/user.store';
import {User} from '@sharedcommon/model/user.model';
import {BehaviorSubject, EMPTY, interval, ObservableInput, Subscription} from 'rxjs';
import {EntityServiceClient, FullAutoImportService, JobStatusInfo, RawMaterialService} from '@sharedrest/data.restservice';
import {catchError, startWith} from 'rxjs/operators';
import {HttpErrorResponse} from '@angular/common/http';
import {NeqDialogService} from '@sharedcommon/services/dialog.service';
import {Rights} from '@sharedcommon/util/rights.util';
import {Constants} from '@sharedcommon/util/constants.util';
import {OperatingModeUtil} from '@sharedcommon/util/operating-mode.util';
import {ActivityUtil} from '@sharedcommon/util/activity.util';
import { JobTaskResponse } from '@sharedcommon/rest/data-custom.restservice';

@Injectable()
export class UserService {

  readonly SUPER_ADMIN = 'SADMIN';
  readonly ADMIN = 'ADMIN';
  readonly RIGHT_ADMIN = 'A_';

  private user: User;
  private metas: UserMetaDto[];
  private activity: string;
  private operatingMode: string;
  private entity: EntityDto;
  private entityLogo: SafeUrl;
  private mainMatrix: MatrixDto;
  private readonly DEFAULT_LANG = 'en';
  private readonly jobId: Map<string, BehaviorSubject<string>> = new Map([
    [Constants.JOB_TYPE_CALCULATE_BATCH, new BehaviorSubject<string>('')],
    [Constants.JOB_TYPE_IMPORT_RM_FULL_AUTO, new BehaviorSubject<string>('')],
    [Constants.JOB_TYPE_MULTI_SAVE, new BehaviorSubject<string>('')]
  ]);
  private readonly jobStatusCalculateBatch: BehaviorSubject<JobStatusInfo> = new BehaviorSubject<JobStatusInfo>({});       // Status used only for CalculateBatch job
  private readonly jobResultCalculateBatch: BehaviorSubject<JobTaskResponse> = new BehaviorSubject<JobTaskResponse>({});   // Result used only for CalculateBatch job
  private readonly jobStatusMultiSave: BehaviorSubject<JobStatusInfo> = new BehaviorSubject<JobStatusInfo>({});            // Status used only for MultiSave job
  private readonly jobResultMultiSave: BehaviorSubject<JobTaskResponse> = new BehaviorSubject<JobTaskResponse>({});        // Result used only for MultiSave job
  private readonly jobSubscription: Map<string, Subscription> = new Map<string, Subscription>();
  private readonly jobSubscriptionImport: Map<string, Subscription> = new Map<string, Subscription>();

  private metas$ = new BehaviorSubject<UserMetaDto[]>(null);

  constructor(
    private readonly store: UserStore,
    private readonly translate: TranslateService,
    private readonly matrixService: MatrixServiceClient,
    private readonly domSanitizer: DomSanitizer,
    private readonly userServiceClient: UserServiceClient,
    private readonly entityService: EntityServiceClient,
    private readonly rawMetarailService: RawMaterialService,
    private readonly dialogService: NeqDialogService,
    private readonly fullAutoImportService: FullAutoImportService,
  ) {
  }

  // when user logout
  clear() {
    this.user = undefined;
    this.metas = undefined;
    this.activity = undefined;
    this.operatingMode = undefined;
    this.entity = undefined;
    this.entityLogo = undefined;
    this.mainMatrix = undefined;
    this.removeJob(Constants.JOB_TYPE_CALCULATE_BATCH);
    this.removeJob(Constants.JOB_TYPE_IMPORT_RM_FULL_AUTO);
    this.removeJob(Constants.JOB_TYPE_MULTI_SAVE);
  }

  removeJob(type: string) {
    this.closeSubscription(type);
    this.jobId.get(type).next('');
    if (type === Constants.JOB_TYPE_CALCULATE_BATCH) {
      this.jobStatusCalculateBatch.next({});
      this.jobResultCalculateBatch.next({});
    } else if (type === Constants.JOB_TYPE_MULTI_SAVE) {
      this.jobStatusMultiSave.next({});
      this.jobResultMultiSave.next({});
    }
  }

  closeSubscription(type: string) {
    if (this.jobSubscription.has(type) && this.jobSubscription.get(type) && !this.jobSubscription.get(type).closed) {
      this.jobSubscription.get(type).unsubscribe();
    }
  }

  setUser(claims: Record<string, string>) {
      try {
        this.user ??= new User();
        this.user.user_uuid = claims.extension_user_uuid;
        this.user.lang_code = claims.extension_user_lang;
        this.user.terms_acceptance = claims.extension_user_terms_acc.substring(1);
        this.user.personal_data_acceptance = claims.extension_user_personal_acc.substring(1)
        this.user.rights = claims.extension_user_rights.split("|");
        this.user.activities = claims.extension_user_activities.split("|");
        this.user.entities = claims.extension_user_entities.split("|");
        this.user.authorities = claims.extension_user_authorities.split("|");
        this.user.email = claims.emails[0];
        this.user.full_name = claims.name;
        this.user.user_name = claims.given_name;
        this.user.licenses = JSON.parse(claims.extension_user_licenses);

        const storedUser = this.store.getUserEntity();
        if (storedUser) {
          const validStoredActivity = storedUser.activity && this.user.activities.some(e => e === storedUser.activity);
          const validStoredOperatingMode = !!storedUser.operatingMode ;
          const validStoredEntity = storedUser.entity && this.user.entities.some(e => e === storedUser.entity.code) ;

          if (validStoredActivity && validStoredOperatingMode) {
            this.setActivity(storedUser.activity);
            this.setOperatingMode(storedUser.operatingMode);
            if (validStoredEntity) {
              this.setEntityDto(storedUser.entity);
            }
          } else {
            this.store.removeUserEntity();
          }
        }

        if (this.user.user_uuid !== "USER_NOT_FOUND") {
          this.userServiceClient.metasFetch().subscribe(metas => {
             this.metas = metas;
             this.metas$.next(metas);
          });
        }
      } catch {
        this.clear();
      }
  }

  getUser(): User {
    return this.user;
  }

  clearStoredUser() {
    this.store.removeUserEntity();
  }


  /**
   * According the lang of the user it return the expected date format
   * @returns the corresponding date format
   */
  returnUserDateFormat(): string {
    return this.user.lang_code === 'fr' ? 'dd/mm/yy' : 'mm/dd/yy';
  }

  saveMeta(key: string, value: string) {
    const userMeta = new UserMetaDto();
    userMeta.key = key;
    userMeta.value = value;

    const index = this.metas.findIndex(x => x.key === userMeta.key);
    if (index > -1) {
      this.metas[index].value = userMeta.value;
    } else {
      this.metas.push(userMeta);
    }

    const patch: PatchDto<string>[] = [
      {op: 'REPLACE', path: '/value', value: userMeta.value}
    ];
    this.userServiceClient.metasPatch(userMeta.key, patch).subscribe();
  }

  removeMeta(key: string) {
    if (this.metas) {
      this.metas = this.metas.filter(meta => meta.key !== key);
    }
  }

  setMetas(metas: UserMetaDto[]) {
    this.metas = metas;
  }

  getMetaValue(key: string): string {
    if (!this.metas) {
      return null;
    }

    const meta = this.metas.find(x => x.key === key);
    return (meta) ? meta.value : null;
  }

  getMetas(): UserMetaDto[] {
    return this.metas;
  }

  isAdmin(): boolean {
    const user = this.getUser();
    const hasAdminAuth = user?.authorities?.some(authority => authority === this.SUPER_ADMIN || authority.startsWith(this.ADMIN));
    return hasAdminAuth || this.hasAdminRight();
  }

  hasRights(askingRights: string[]): boolean {
    const user = this.getUser();

    if (!user || !this.getActivity()) {
      return false;
    }

    const activityPrefixedAsked = askingRights.map(right => `${this.getActivity()}@${right}`);
    const hasRights = intersection(user.rights, activityPrefixedAsked).length !== 0;
    return this.isSuperAdmin() || this.isAdminOfCurrentActivity() || hasRights;
  }

  setActivity(code: string) {
    this.activity = code;
  }

  getActivity(): string {
    return this.activity;
  }

  getOperatingMode(): string {
    return this.operatingMode;
  }

  setOperatingMode(operatingMode: string): void {
    this.operatingMode = operatingMode;
  }

  setEntityDto(entity: EntityDto) {
    this.entity = entity;

    if (this.entity?.logo) {
      this.entityLogo = this.domSanitizer.bypassSecurityTrustUrl(this.entity.logo);
    } else {
      this.entityLogo = undefined;
    }

    if (this.entity?.principalMatrix) {
      this.matrixService.getByCode(this.entity.principalMatrix.identifier).subscribe(matrix => this.mainMatrix = matrix);
    }

    this.store.setUserEntity({activity: this.activity, operatingMode: this.operatingMode, entity: this.entity});
  }

  getEntityDto(): EntityDto {
    return this.entity;
  }

  getEntity(): string {
    return (this.entity) ? this.entity.code : null;
  }

  getEntityLogo(): SafeUrl {
    return this.entityLogo;
  }

  getMainMatrix(): MatrixDto {
    return this.mainMatrix;
  }

  manageLang(setLang = false) {
    this.translate.addLangs(environment.translate.supportedLang);
    this.translate.setDefaultLang(this.DEFAULT_LANG);
    if (setLang) {
      if (FunctionUtils.isDefined(this.getUser())) {

        if (this.getUser().lang_code.length === 2) {
          this.setLang(this.getUser().lang_code);
        } else {
          this.setLang(this.DEFAULT_LANG);
        }
      } else {
        // Take the langage of the navigator if the user is not connected.
        let lang = '';
        if (navigator.language?.includes('-')) {
          lang = navigator.language.substring(0, navigator.language.indexOf('-'));
        } else {
          lang = navigator.language;
        }
        this.setLang(lang);
      }
    }
  }

  changeLang() {
    const lang = this.getUser().lang_code;
    if (FunctionUtils.isDefined(lang) && lang.length === 2) {
      this.setLang(lang);
    } else {
      console.error('Bad lang code ', lang);
    }
  }

  private setLang(lang: string) {
    this.translate.use(lang);
    moment.locale(lang);
  }

  isGdprAccept(): boolean {
    const user = this.getUser();
    return (user.terms_acceptance !== null) && (user.terms_acceptance !== "");
  }

  isSampleOperatingMode(): boolean {
    return OperatingModeUtil.isSampleOperatingMode(this.operatingMode);
  }

  isFeedOperatingMode(): boolean {
    return OperatingModeUtil.isFeedOperatingMode(this.operatingMode);
  }

  isRmActivityCode(): boolean {
    return ActivityUtil.isRmMode(this.activity);
  }

  isFodderActivityCode(): boolean {
    return ActivityUtil.isFodderActivityCode(this.activity);
  }

  isSuperAdmin(): boolean {
    const user = this.getUser();
    return user && some(user.authorities, authority => authority === this.SUPER_ADMIN);
  }

  private hasAdminRight(): boolean {
    const user = this.getUser();
    return user?.rights.some(right => /^[A-Za-z0-9]{2}@A_.+/.test(right));
  }

  hasAdminRightOfActivity(activity: string): boolean {
    const user = this.getUser();
    return user?.rights.some(right => right.startsWith(activity + '@' + this.RIGHT_ADMIN));
  }

  private isAdminOfCurrentActivity(): boolean {
    return this.isAdminOfActivity(this.getActivity());
  }

  isAdminOfActivity(activity: string): boolean {
    const user = this.getUser();
    return user?.authorities.some(authority => authority === this.ADMIN + '_' + activity || authority === this.SUPER_ADMIN);
  }

  canChangeEntity(): boolean {
    if (!this.user) {
      return false;
    }

    const hasMultipleEntities = this.user.entities.length > 1;
    const hasMultipleActivities = this.user.activities.length > 1;

    return this.isSuperAdmin() || hasMultipleEntities || hasMultipleActivities;
  }

  refreshJobs() {
    // get executing job
    if (this.hasRights([Rights.U_MP_CALCULATE_BATCH])) {
      this.entityService.rawMaterialscalculateBatchGetJobs(this.getEntity(), Constants.JOB_TYPE_CALCULATE_BATCH).subscribe(jobs => {
        if (jobs.length > 0) {
          this.addJob(jobs[0].id, Constants.JOB_TYPE_CALCULATE_BATCH);
        }
      });
    }
  }

  addJob(id: string, type: string) {
    let jobSubscription: Subscription;
    this.jobId.get(type).next(id);

    if (type === Constants.JOB_TYPE_CALCULATE_BATCH) {
      this.jobStatusCalculateBatch.next({status: 'PENDING'});
      jobSubscription = interval(5000)
                        .pipe(startWith(0))
                        .subscribe(_value => this.calculationBatchJob(id));
    } else if (type == Constants.JOB_TYPE_IMPORT_RM_FULL_AUTO){
      jobSubscription = interval(5000)
                        .pipe(startWith(0))
                        .subscribe(_value => this.importRmFeedJob(id));
      this.jobSubscriptionImport.set(id, jobSubscription);
    } else if (type == Constants.JOB_TYPE_MULTI_SAVE) {
      this.jobStatusMultiSave.next({status: 'PENDING'});
      jobSubscription = interval(2000)
                        .pipe(startWith(0))
                        .subscribe(_value => this.multiSaveRmJob(id));
    }
    this.jobSubscription.set(type, jobSubscription);
  }

  private calculationBatchJob(id: string) {
    this.entityService.rawMaterialscalculateBatchStatusGetStatus(this.getEntity(), id).pipe(
      catchError((err: HttpErrorResponse, _caught: ObservableInput<any>) => {
        if (err.status === 303) {
          this.closeSubscription(Constants.JOB_TYPE_CALCULATE_BATCH);
          this.entityService.rawMaterialscalculateBatchResultGetResult(this.getEntity(), id).subscribe(result => {
            this.jobStatusCalculateBatch.next({status: 'FINISHED'});
            this.jobResultCalculateBatch.next(result);
          });
        } else {
          this.dialogService.showErrorDetail(err, this.translate.instant('error.common.technical'));
        }
        return EMPTY;
      })).subscribe(status => {
        this.jobStatusCalculateBatch.next(status);
    });
  }

  private importRmFeedJob(id: string) {
    this.fullAutoImportService.importRmFeedFullAutoStatusGetStatus(this.getEntity(), id).pipe(
      catchError((err: HttpErrorResponse, _caught: ObservableInput<any>) => {
        if (err.status === 303) {
          this.jobSubscriptionImport.get(id).unsubscribe();
          this.jobSubscriptionImport.delete(id);
          if (this.jobSubscriptionImport.size == 0) {
            this.removeJob(Constants.JOB_TYPE_IMPORT_RM_FULL_AUTO);
          } else {
            this.jobSubscription.set(Constants.JOB_TYPE_IMPORT_RM_FULL_AUTO, this.jobSubscriptionImport.values[0])
          }
        } else {
          this.dialogService.showErrorDetail(err, this.translate.instant('error.common.technical'));
        }
        return EMPTY;
      })).subscribe(_status => {
        // do nothing with status
    });
  }

  private multiSaveRmJob(id: string) {
    this.rawMetarailService.rawMaterialsMultipleSaveBatchStatusGetStatus(this.getEntity(), id).pipe(
      catchError((err: HttpErrorResponse, _caught: ObservableInput<any>) => {
        if (err.status === 303) {
          this.closeSubscription(Constants.JOB_TYPE_MULTI_SAVE);
          this.rawMetarailService.rawMaterialsMultipleSaveBatchResultGetResult(this.getEntity(), id).subscribe(result => {
            this.jobStatusMultiSave.next({status: 'FINISHED'});
            this.jobResultMultiSave.next(result);
          });
        } else {
          this.dialogService.showErrorDetail(err, this.translate.instant('error.common.technical'));
        }
        return EMPTY;
      })).subscribe(status => {
        this.jobStatusMultiSave.next(status);
    });
  }

  getJobId(type: string): BehaviorSubject<string> {
    return this.jobId.get(type);
  }

  getJobStatus(type: string): BehaviorSubject<JobStatusInfo> {
    return type == Constants.JOB_TYPE_CALCULATE_BATCH ? this.jobStatusCalculateBatch : this.jobStatusMultiSave;
  }

  getJobResult(type: string): BehaviorSubject<JobTaskResponse> {
    return type == Constants.JOB_TYPE_CALCULATE_BATCH ? this.jobResultCalculateBatch : this.jobResultMultiSave;
  }

  getMetasObservable(): Observable<UserMetaDto[]> {
    return this.metas$.asObservable();
  }

}
